import React from 'react';
import { useProgramFixedContentRows } from './useProgramFixedContentRows';
import { useAnnualProgressPageQueries } from './useAnnualProgressPageQueries';
import { getAnnualProjectPrograms } from '../services/annualProjectProgramsService';
import { getGradeTrackSections, type PageSectionLayout } from '../services/sectionLayoutService';
import {
  ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT,
  readAnnualProjectProgramsCache,
  type AnnualProjectProgram,
} from '../utils/annualProjectProgramsStorage';
import {
  AnnualClassEntry,
  buildAnnualClassEntries,
} from '../utils/annualTimetable';
import {
  extractAnnualProgressCrossTopicDetailRows,
  type AnnualCrossTopicDetailRow,
} from '../utils/annualProgressCrossTopicExtract';
import { loadTrackProgramModeMap } from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import type { Grade, Track } from '../types/domain';

export type UseAnnualProgressCrossTopicsForTermParams = {
  enabled: boolean;
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  termNumber: 1 | 2;
  programGradeId: string;
  schoolScopedCacheSyncReady: boolean;
  /** 저장 등으로 DB 재집계가 필요할 때 증가 */
  refreshNonce?: number;
  /** true면 범교과가 빈 수업 행도 포함 */
  includeEmptyCross?: boolean;
};

export function useAnnualProgressCrossTopicsForTerm(
  params: UseAnnualProgressCrossTopicsForTermParams
): { values: string[]; detailRows: AnnualCrossTopicDetailRow[]; loading: boolean } {
  const {
    enabled,
    workspaceId,
    grades,
    academicYear,
    termNumber,
    programGradeId,
    schoolScopedCacheSyncReady,
    refreshNonce = 0,
    includeEmptyCross = false,
  } = params;

  const apPageData = useAnnualProgressPageQueries({
    workspaceId: enabled && schoolScopedCacheSyncReady ? workspaceId : '',
    grades,
    academicYear,
    termNumber,
    selectedGradeId: programGradeId,
    metaGradeId: programGradeId,
    isActive: enabled,
    schoolScopedCacheSyncReady: enabled && schoolScopedCacheSyncReady,
    annualTimetableSaveReloadToken: 0,
    programRefreshTick: refreshNonce,
    annualAggregateRefreshNonce: refreshNonce,
    programCellsFetchNonce: refreshNonce,
  });

  const schedule = apPageData.schedule;
  const tracks = apPageData.tracks;
  const terms = apPageData.terms;
  const termDef = React.useMemo(
    () => terms.find((t) => t.term_number === termNumber) ?? null,
    [terms, termNumber]
  );

  const classEntries = React.useMemo(() => buildAnnualClassEntries(schedule, grades), [schedule, grades]);
  const selectedEntry = React.useMemo<AnnualClassEntry | null>(() => {
    const gid = programGradeId.trim();
    if (!gid) return null;
    return classEntries.find((e) => e.gradeId === gid) ?? null;
  }, [classEntries, programGradeId]);

  const [gradeTrackSections, setGradeTrackSections] = React.useState<PageSectionLayout[]>([]);
  React.useEffect(() => {
    if (!enabled || !workspaceId.trim()) {
      setGradeTrackSections([]);
      return;
    }
    let cancelled = false;
    void getGradeTrackSections(workspaceId)
      .then((rows) => {
        if (!cancelled) setGradeTrackSections(rows);
      })
      .catch(() => {
        if (!cancelled) setGradeTrackSections([{ id: 'base', gradeIds: [] }]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId]);

  const [annualProjectProgramsForSelectedGrade, setAnnualProjectProgramsForSelectedGrade] = React.useState<
    AnnualProjectProgram[]
  >([]);
  React.useEffect(() => {
    const gid = selectedEntry?.gradeId ?? '';
    if (!enabled || !workspaceId || !gid) {
      setAnnualProjectProgramsForSelectedGrade([]);
      return;
    }
    let cancelled = false;
    setAnnualProjectProgramsForSelectedGrade(readAnnualProjectProgramsCache(workspaceId, gid, academicYear));
    void getAnnualProjectPrograms(workspaceId, gid, academicYear)
      .then((rows) => {
        if (!cancelled) setAnnualProjectProgramsForSelectedGrade(rows);
      })
      .catch(() => {});
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string; gradeId?: string; academicYear?: number }>).detail;
      if (detail?.workspaceId !== workspaceId || detail?.gradeId !== gid || detail?.academicYear !== academicYear) {
        return;
      }
      setAnnualProjectProgramsForSelectedGrade(readAnnualProjectProgramsCache(workspaceId, gid, academicYear));
    };
    window.addEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onChanged as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onChanged as EventListener);
    };
  }, [academicYear, enabled, selectedEntry?.gradeId, workspaceId]);

  const trackNameByTimetableTextByGrade = React.useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    tracks.forEach((row: Track) => {
      const gradeId = (row.grade_id ?? '').trim();
      const trackName = (row.name ?? '').trim();
      const timetableName = (row.description ?? '').trim();
      if (!gradeId || !trackName) return;
      if (!map[gradeId]) map[gradeId] = {};
      map[gradeId][trackName] = trackName;
      if (timetableName) map[gradeId][timetableName] = trackName;
    });
    return map;
  }, [tracks]);

  const trackProgramModeByTrack = React.useMemo(() => {
    const gid = selectedEntry?.gradeId ?? '';
    if (!workspaceId || !gid) return {};
    return loadTrackProgramModeMap(workspaceId, gid);
  }, [selectedEntry?.gradeId, workspaceId]);

  const trackProgramSyncRepByTrack = React.useMemo(() => {
    const gid = selectedEntry?.gradeId ?? '';
    if (!workspaceId || !gid) return {};
    return loadTrackProgramSyncRepMap(workspaceId, gid);
  }, [selectedEntry?.gradeId, workspaceId]);

  const { loading: fixedContentRowsLoading, rows: fixedContentRows } = useProgramFixedContentRows({
    workspaceId: enabled && schoolScopedCacheSyncReady ? workspaceId : '',
    academicYear,
    termNumber,
    gradeId: selectedEntry?.gradeId ?? '',
    refreshNonce: 0,
  });

  const loading =
    enabled &&
    (apPageData.metaLoading ||
      apPageData.annualCellsLoading ||
      apPageData.programDataLoading ||
      fixedContentRowsLoading ||
      !termDef ||
      !selectedEntry);

  const detailRows = React.useMemo(() => {
    if (!enabled || !selectedEntry || !termDef) return [];
    return extractAnnualProgressCrossTopicDetailRows({
      workspaceId,
      academicYear,
      termNumber,
      selectedEntry,
      termDef,
      cellsByKey: apPageData.cellsByKey,
      programCellsByKey: apPageData.programCellsByKey,
      scopedRowsForTerm: apPageData.scopedRowsForTerm,
      fixedContentRows,
      holidaySet: apPageData.holidaySet,
      offDayByDate: apPageData.offDayByDate,
      dedicatedColors: apPageData.dedicatedColors,
      baseExcludeColors: apPageData.baseExcludeColors,
      regularColors: apPageData.regularColors,
      annualProjectProgramsForSelectedGrade,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
      tracks,
      gradeTrackSections,
      trackNameByTimetableTextByGrade,
      includeEmptyCross,
    });
  }, [
    includeEmptyCross,
    academicYear,
    annualProjectProgramsForSelectedGrade,
    apPageData.baseExcludeColors,
    apPageData.cellsByKey,
    apPageData.dedicatedColors,
    apPageData.holidaySet,
    apPageData.offDayByDate,
    apPageData.programCellsByKey,
    apPageData.regularColors,
    apPageData.scopedRowsForTerm,
    enabled,
    fixedContentRows,
    gradeTrackSections,
    selectedEntry,
    termDef,
    termNumber,
    trackNameByTimetableTextByGrade,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack,
    tracks,
    workspaceId,
  ]);

  const values = React.useMemo(
    () => detailRows.map((row) => row.crossTopic).filter((v) => !!v.trim()),
    [detailRows]
  );

  return { values, detailRows, loading };
}
